import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { NewConversationModal } from '../components/NewConversationModal';
import { ContextMenu, type MenuNode } from '../components/ContextMenu';
import { useAuth } from '../hooks/useAuth';
import { useOutsideClick } from '../hooks/useOutsideClick';
import {
  directoryApi,
  inboxApi,
  getLatestChatForContact,
  isOlamiContact,
  type ContactRow,
} from '../lib/api';
import { t, useLocale } from '../lib/i18n';
import {
  Filter,
  Search,
  Settings,
  Plus,
  Download,
  RefreshCw,
  ShieldCheck,
  Ban,
  Loader2,
  MoreVertical,
  MessageSquarePlus,
  MessageSquare,
  StickyNote,
  Trash2,
  Eye,
  EyeOff,
  UserX,
} from 'lucide-react';

type SortBy = 'name' | 'createdAt' | 'lastSeenAt' | 'email';
type Order = 'asc' | 'desc';
type FilterChoice = '' | 'yes' | 'no';

/**
 * With a filter chip on, an equality filter + orderBy needs a dedicated
 * composite index per (field, direction). Only six exist — {lastSeenAt,
 * createdAt} x DESC across the three filter shapes — so the server caps the
 * matrix to those and alphabetises name/email in memory over the page.
 * Mirroring the cap here keeps the header arrows honest instead of showing an
 * ascending arrow the server will not honour. See src/lib/contacts-sort.ts.
 */
const FILTERED_QUERY_SORT_FIELDS: SortBy[] = ['lastSeenAt', 'createdAt'];

interface FilterState {
  verified: FilterChoice;
  blocked: FilterChoice;
}

const PAGE_SIZE = 50;

/**
 * "Show anonymous" is OFF by default: a widget visitor with no trusted
 * identifier gets a throwaway `anon-<ts>` contact per session, so hundreds of
 * unreachable rows accumulate and — because the directory sorts by lastSeenAt
 * DESC — they bury every real contact at the top of the page.
 *
 * The preference is persisted per browser profile under the page's usual
 * `chatolam:` localStorage contract (see uiMode.ts, InboxPage's listWidth).
 * The exclusion itself happens SERVER-SIDE in the Firestore query, so a page of
 * 50 is 50 real contacts and `nextCursor` still means what it says.
 */
const SHOW_ANON_KEY = 'chatolam:contacts:showAnonymous';

function readShowAnonymous(): boolean {
  try {
    return localStorage.getItem(SHOW_ANON_KEY) === '1';
  } catch {
    return false;
  }
}

function writeShowAnonymous(v: boolean): void {
  try {
    localStorage.setItem(SHOW_ANON_KEY, v ? '1' : '0');
  } catch {
    /* ignore — the toggle still works for this session */
  }
}

function formatRelative(ms?: number): string {
  if (!ms) return '--';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const y = Math.floor(mo / 12);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

export function ContactsPage() {
  const navigate = useNavigate();
  useLocale(); // re-render on language switch so t() below stays current
  const { user, claims, signOut } = useAuth();
  const tenantCode = claims?.tenantCode;
  const [items, setItems] = useState<ContactRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasLoadedFirst, setHasLoadedFirst] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('lastSeenAt');
  const [order, setOrder] = useState<Order>('desc');
  const [filters, setFilters] = useState<FilterState>({ verified: '', blocked: '' });
  const [showAnonymous, setShowAnonymous] = useState<boolean>(readShowAnonymous);
  // What the server did about anonymous rows on the last first-page load.
  // hiddenCount null = it could not be counted; markerReady false = the
  // `anonymous` field is not populated in this tenant yet, so nothing was hidden.
  const [anonymity, setAnonymity] = useState<{
    hidden: boolean;
    hiddenCount: number | null;
    markerReady: boolean;
  }>({ hidden: false, hiddenCount: null, markerReady: true });
  const [loadError, setLoadError] = useState<string | null>(null);
  // 'page' = the server could only order the rows it returned (name/email under
  // a filter), so the sort is not collection-wide.
  const [sortScope, setSortScope] = useState<'query' | 'page'>('query');
  // Hiding anonymous contacts is an equality filter (`anonymous == false`) just
  // like the chips, so it constrains the sort matrix the same way — and since
  // it is on by default, the DEFAULT listing is a filtered one.
  const hasFilter = filters.verified !== '' || filters.blocked !== '' || !showAnonymous;
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Row click -> jump to the contact's most-recent conversation.
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // New-conversation modal, optionally prefilled with a contact.
  const [composeFor, setComposeFor] = useState<ContactRow | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  // Per-row actions menu (cursor-positioned ContextMenu).
  const [menu, setMenu] = useState<{ x: number; y: number; contact: ContactRow } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const filterMenuRef = useOutsideClick<HTMLDivElement>(filterMenuOpen, () => setFilterMenuOpen(false));
  const bulkMenuRef = useOutsideClick<HTMLDivElement>(bulkMenuOpen, () => setBulkMenuOpen(false));

  const isSuperAdmin = claims?.role === 'super_admin';

  // Hide internal Olami staff contacts from non-super-admins (client guard;
  // the /agent/contacts endpoint applies the same exclusion server-side).
  //
  // When the server reports `scope: 'page'` it alphabetised each page on its
  // own, so after "load more" the merged list would be A-Z, then A-Z again.
  // Re-sort the accumulation here so the banner's claim stays true.
  const visibleItems = useMemo(() => {
    const base = isSuperAdmin ? items : items.filter((c) => !isOlamiContact(c));
    if (sortScope !== 'page') return base;
    const dir = order === 'asc' ? 1 : -1;
    const key = (c: ContactRow) => (c as unknown as Record<string, unknown>)[sortBy];
    return [...base].sort((a, b) => {
      const av = key(a);
      const bv = key(b);
      const aBlank = av === undefined || av === null || av === '';
      const bBlank = bv === undefined || bv === null || bv === '';
      if (aBlank !== bBlank) return aBlank ? 1 : -1; // blanks last, both ways
      if (aBlank) return 0;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
    });
  }, [items, isSuperAdmin, sortScope, sortBy, order]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ── Row actions ──────────────────────────────────────────────────
  function composeForContact(c: ContactRow) {
    setComposeFor(c);
    setComposeOpen(true);
  }

  async function toggleBlock(c: ContactRow) {
    if (!tenantCode || !user?.uid || busyId) return;
    setBusyId(c.id);
    const next = !c.blocked;
    try {
      if (next) await inboxApi.blockContact(tenantCode, user.uid, c.id);
      else await inboxApi.unblockContact(tenantCode, user.uid, c.id);
      setItems((cur) => cur.map((x) => (x.id === c.id ? { ...x, blocked: next } : x)));
      setToast(next ? 'Contact blocked.' : 'Contact unblocked.');
    } catch (e) {
      console.error('block toggle failed', e);
      setToast('Could not update block state. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function addNote(c: ContactRow) {
    if (!tenantCode || !user?.uid || busyId) return;
    const note = window.prompt(`Add a note for ${c.name || c.email || c.phone || 'this contact'}:`);
    if (!note || !note.trim()) return;
    setBusyId(c.id);
    try {
      await inboxApi.addContactNote(tenantCode, user.uid, c.id, note.trim());
      setToast('Note added.');
    } catch (e) {
      console.error('add note failed', e);
      setToast('Could not add note. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function deleteContact(c: ContactRow) {
    if (!tenantCode || busyId) return;
    const display = c.name || c.email || c.phone || c.id;
    if (!window.confirm(`Delete contact "${display}"? This cannot be undone.`)) return;
    setBusyId(c.id);
    try {
      await directoryApi.deleteContact(tenantCode, c.id);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
      setSelected((cur) => {
        if (!cur.has(c.id)) return cur;
        const nx = new Set(cur);
        nx.delete(c.id);
        return nx;
      });
      setToast('Contact deleted.');
    } catch (e) {
      console.error('delete contact failed', e);
      setToast('Could not delete contact. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  // ── Bulk actions on the checked rows ─────────────────────────────
  async function bulkSetBlocked(blocked: boolean) {
    if (!tenantCode || !user?.uid || selected.size === 0 || bulkBusy) return;
    setBulkMenuOpen(false);
    setBulkBusy(true);
    const ids = [...selected];
    try {
      await Promise.all(
        ids.map((id) =>
          blocked ? inboxApi.blockContact(tenantCode, user!.uid, id) : inboxApi.unblockContact(tenantCode, user!.uid, id)
        )
      );
      const idSet = new Set(ids);
      setItems((cur) => cur.map((x) => (idSet.has(x.id) ? { ...x, blocked } : x)));
      setSelected(new Set());
      setToast(blocked ? `Blocked ${ids.length} contact(s).` : `Unblocked ${ids.length} contact(s).`);
    } catch (e) {
      console.error('bulk block failed', e);
      setToast('Some contacts could not be updated. Try again.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkDelete() {
    if (!tenantCode || selected.size === 0 || bulkBusy) return;
    setBulkMenuOpen(false);
    if (!window.confirm(`Delete ${selected.size} contact(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => directoryApi.deleteContact(tenantCode, id)));
      const idSet = new Set(ids);
      setItems((cur) => cur.filter((x) => !idSet.has(x.id)));
      setSelected(new Set());
      setToast(`Deleted ${ids.length} contact(s).`);
    } catch (e) {
      console.error('bulk delete failed', e);
      setToast('Some contacts could not be deleted. Try again.');
    } finally {
      setBulkBusy(false);
    }
  }

  function buildRowMenu(c: ContactRow): MenuNode[] {
    return [
      {
        id: 'compose',
        label: 'New conversation',
        icon: <MessageSquarePlus className="w-3.5 h-3.5" />,
        onSelect: () => composeForContact(c),
      },
      {
        id: 'open-latest',
        label: 'Open latest conversation',
        icon: <MessageSquare className="w-3.5 h-3.5" />,
        onSelect: () => void openContact(c),
      },
      { divider: true },
      {
        id: 'block',
        label: c.blocked ? 'Unblock contact' : 'Block contact',
        icon: <Ban className="w-3.5 h-3.5" />,
        onSelect: () => void toggleBlock(c),
      },
      {
        id: 'note',
        label: 'Add note',
        icon: <StickyNote className="w-3.5 h-3.5" />,
        onSelect: () => void addNote(c),
      },
      { divider: true },
      {
        id: 'delete',
        label: 'Delete contact',
        icon: <Trash2 className="w-3.5 h-3.5" />,
        danger: true,
        onSelect: () => void deleteContact(c),
      },
    ];
  }

  async function openContact(c: ContactRow) {
    if (openingId) return;
    setOpeningId(c.id);
    try {
      const chatId = await getLatestChatForContact(c.id);
      if (chatId) {
        navigate(`/inbox/${chatId}`);
      } else {
        // No conversation yet — offer to start one, prefilled with this contact.
        const display = c.name || c.email || c.phone || 'this contact';
        setToast(`No conversation for ${display} yet — starting a new one.`);
        setComposeFor(c);
        setComposeOpen(true);
      }
    } catch (e) {
      console.error('open contact failed', e);
      setToast('Could not open conversation. Try again.');
    } finally {
      setOpeningId(null);
    }
  }

  // Debounce search
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  // Turning a filter on while sorted ascending by a date column: snap the
  // direction back to desc so the header arrow matches what the server can
  // actually serve (see FILTERED_QUERY_SORT_FIELDS).
  useEffect(() => {
    if (hasFilter && order === 'asc' && FILTERED_QUERY_SORT_FIELDS.includes(sortBy)) {
      setOrder('desc');
    }
  }, [hasFilter, order, sortBy]);

  // Reload on filter / sort / debouncedSearch change
  useEffect(() => {
    if (!tenantCode) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    directoryApi
      .listContacts(tenantCode, {
        q: debouncedSearch || undefined,
        sortBy,
        order,
        limit: PAGE_SIZE,
        verified: filters.verified || undefined,
        blocked: filters.blocked || undefined,
        includeAnonymous: showAnonymous ? 'yes' : 'no',
      })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setCursor(r.nextCursor);
        setHasLoadedFirst(true);
        setSortScope(r.sort?.scope ?? 'query');
        setAnonymity(r.anonymity ?? { hidden: false, hiddenCount: null, markerReady: true });
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('contacts list failed', e);
          setItems([]);
          // An empty table used to be the ONLY symptom of a failed query —
          // which is how a missing index hid here for months. Say it failed.
          setLoadError('Could not load contacts. The list below is not your data.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, debouncedSearch, sortBy, order, filters.verified, filters.blocked, showAnonymous]);

  async function loadMore() {
    if (!tenantCode || !cursor || loading) return;
    setLoading(true);
    try {
      const r = await directoryApi.listContacts(tenantCode, {
        q: debouncedSearch || undefined,
        sortBy,
        order,
        limit: PAGE_SIZE,
        cursor,
        verified: filters.verified || undefined,
        blocked: filters.blocked || undefined,
        includeAnonymous: showAnonymous ? 'yes' : 'no',
      });
      setItems((cur) => [...cur, ...r.items]);
      setCursor(r.nextCursor);
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(col: SortBy) {
    // Under a filter, a date column has no ascending index: don't offer a
    // direction the server would have to coerce back to desc.
    const directionLocked = hasFilter && FILTERED_QUERY_SORT_FIELDS.includes(col);
    if (sortBy === col) {
      if (!directionLocked) setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setOrder('desc');
    }
  }

  function toggleShowAnonymous() {
    setShowAnonymous((v) => {
      const next = !v;
      writeShowAnonymous(next);
      return next;
    });
    setSelected(new Set());
  }

  // "Show me everything again". Only lifts the anonymity filter when it is
  // actually hiding something — clearing the chips should not flip a toggle
  // that had no effect.
  function clearAllFilters() {
    setFilters({ verified: '', blocked: '' });
    if (anonFilterActive) toggleShowAnonymous();
  }

  function toggleSelect(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelected((cur) => {
      if (cur.size === visibleItems.length) return new Set();
      return new Set(visibleItems.map((c) => c.id));
    });
  }

  const visibleCount = visibleItems.length;
  const totalLabel = hasLoadedFirst ? `${visibleCount}${cursor ? '+' : ''}` : '…';
  // Rows the anonymity filter is keeping off the screen right now. null when the
  // server could not count them — then we say "hidden" without a number rather
  // than inventing one.
  const hiddenAnonCount = !showAnonymous && anonymity.hidden ? anonymity.hiddenCount : null;
  const anonFilterActive = !showAnonymous && anonymity.hidden;
  const canClearFilters = filters.verified !== '' || filters.blocked !== '' || anonFilterActive;

  const sortIndicator = useMemo(
    () => (col: SortBy) => (sortBy === col ? (order === 'desc' ? ' ↓' : ' ↑') : ''),
    [sortBy, order]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar tenantCode={tenantCode ?? ''} userEmail={user?.email ?? undefined} onSignOut={signOut} />

      <div className="pt-10 px-6 pb-6">
        {/* Header row */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-baseline gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">Contacts</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                // simple "refresh" by resetting filters/sort
                setDebouncedSearch((s) => s);
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
              title="Reload"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => alert('Customize table — coming soon')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
            >
              <Settings className="w-3.5 h-3.5" /> Customize table
            </button>
            <button
              onClick={() => alert('Import contacts — coming soon')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50"
            >
              <Download className="w-3.5 h-3.5 rotate-180" /> Import contacts
            </button>
            <button
              onClick={() => {
                setComposeFor(null);
                setComposeOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
            >
              <Plus className="w-3.5 h-3.5" /> New conversation
            </button>
          </div>
        </div>

        {/* Tabs row (single "All contacts" tab for now) */}
        <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
          <button className="px-3 py-1 text-sm font-medium text-gray-900 border-b-2 border-primary-600 -mb-2.5">
            All contacts
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">
              {totalLabel}
            </span>
          </button>
        </div>

        {/* Toolbar row */}
        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative w-64">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts (name / email / phone / sub)"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <span className="text-xs text-gray-300">|</span>
            <div className="relative" ref={bulkMenuRef}>
              <button
                onClick={() => selected.size > 0 && setBulkMenuOpen((v) => !v)}
                disabled={selected.size === 0 || bulkBusy}
                title={selected.size === 0 ? 'Select one or more contacts first' : 'Actions for the selected contacts'}
                className="px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Bulk actions {selected.size > 0 && <span className="text-primary-700 font-semibold">({selected.size})</span>}
              </button>
              {bulkMenuOpen && selected.size > 0 && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg w-48 z-10 py-1 text-xs">
                  <button
                    onClick={() => bulkSetBlocked(true)}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <Ban className="w-3.5 h-3.5" /> Block {selected.size}
                  </button>
                  <button
                    onClick={() => bulkSetBlocked(false)}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" /> Unblock {selected.size}
                  </button>
                  {isSuperAdmin && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={bulkDelete}
                        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-red-50 text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Anonymous visitors: hidden by default, never silently. The count
                badge is the "the filter is ON" signal — a directory that just
                looks short is how this class of bug hides. */}
            <button
              onClick={toggleShowAnonymous}
              aria-pressed={showAnonymous}
              title={
                showAnonymous
                  ? t('contacts.anon.showing')
                  : t('contacts.anon.hiddenUnknown')
              }
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border ${
                showAnonymous
                  ? 'text-primary-800 bg-primary-50 border-primary-200 hover:bg-primary-100'
                  : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              {showAnonymous ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {showAnonymous ? t('contacts.anon.hide') : t('contacts.anon.show')}
              {hiddenAnonCount !== null && hiddenAnonCount > 0 && (
                <span className="ml-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                  {hiddenAnonCount}
                </span>
              )}
            </button>
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => setFilterMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50"
              >
                <Filter className="w-3.5 h-3.5" /> Filter by
                {(filters.verified || filters.blocked) && (
                  <span className="ml-1 inline-block w-1.5 h-1.5 bg-primary-600 rounded-full" />
                )}
              </button>
              {filterMenuOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg w-56 z-10 p-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1 mb-1">
                    Identity
                  </div>
                  <FilterTri label="Verified" value={filters.verified} onChange={(v) => setFilters({ ...filters, verified: v })} />
                  <FilterTri label="Blocked" value={filters.blocked} onChange={(v) => setFilters({ ...filters, blocked: v })} />
                  <div className="border-t border-gray-100 mt-2 pt-2 flex justify-end">
                    <button
                      onClick={() => setFilters({ verified: '', blocked: '' })}
                      className="text-xs text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {loadError && (
          <div className="mt-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
            {loadError}
          </div>
        )}
        {/* The `anonymous` marker isn't in this tenant's data yet, so the server
            listed everything rather than serving a blank directory. */}
        {!loadError && !showAnonymous && !anonymity.markerReady && hasLoadedFirst && (
          <div className="mt-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded">
            {t('contacts.anon.notReady')}
          </div>
        )}
        {!loadError && anonFilterActive && hiddenAnonCount !== null && hiddenAnonCount > 0 && (
          <div className="mt-3 px-3 py-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5">
              <UserX className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {t('contacts.anon.hiddenSome', { n: hiddenAnonCount, s: hiddenAnonCount === 1 ? '' : 's' })}
            </span>
            <button
              onClick={toggleShowAnonymous}
              className="flex-shrink-0 text-xs font-medium text-primary-700 hover:text-primary-900 underline"
            >
              {t('contacts.anon.show')}
            </button>
          </div>
        )}
        {!loadError && sortScope === 'page' && (
          <div className="mt-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded">
            Sorted by {sortBy === 'email' ? 'email' : 'name'} within the {visibleItems.length} loaded
            row{visibleItems.length === 1 ? '' : 's'} only — filtered lists are paged by most recent
            activity. Load more rows, or clear the filter, for a full A-Z.
          </div>
        )}

        {/* Table */}
        <div className="mt-4 bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-gray-500">
                <th className="w-10 px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={visibleItems.length > 0 && selected.size === visibleItems.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <Th onClick={() => toggleSort('name')} label={`Name${sortIndicator('name')}`} />
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Last seen on chat</th>
                <Th onClick={() => toggleSort('email')} label={`Email${sortIndicator('email')}`} />
                <Th onClick={() => toggleSort('createdAt')} label={`Created at${sortIndicator('createdAt')}`} />
                <Th onClick={() => toggleSort('lastSeenAt')} label={`Last activity${sortIndicator('lastSeenAt')}`} />
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="w-10 px-3 py-2 text-right font-medium text-xs uppercase tracking-wide" />
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((c) => {
                const isSelected = selected.has(c.id);
                const display = c.name || (c.id.startsWith('anon-') ? 'Anonymous' : c.id);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-amber-50' : ''} ${openingId === c.id ? 'opacity-60' : ''}`}
                    onClick={() => openContact(c)}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(c.id)} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-600 flex-shrink-0">
                          {openingId === c.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />
                          ) : (
                            (display || '?').slice(0, 2).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate" dir="auto">{display}</div>
                          {c.phone && (
                            <div className="text-[11px] text-gray-500 font-mono" dir="ltr">{c.phone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">--</td>
                    <td className="px-3 py-2.5 text-xs text-primary-700 truncate max-w-[220px]">
                      {c.email || <span className="text-gray-400">Add email</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{formatRelative(c.createdAt)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{formatRelative(c.lastSeenAt ?? c.updatedAt)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {c.verified && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                            <ShieldCheck className="w-3 h-3" /> verified
                          </span>
                        )}
                        {c.blocked && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                            <Ban className="w-3 h-3" /> blocked
                          </span>
                        )}
                        {/* `anonymous` is authoritative when present; before the
                            backfill lands it is undefined, so fall back to the
                            id shape /widget/init mints. */}
                        {(c.anonymous ?? c.id.startsWith('anon-')) && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            <UserX className="w-3 h-3" /> {t('contacts.anon.badge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        title="Contact actions"
                        disabled={busyId === c.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu({ x: r.right, y: r.bottom, contact: c });
                        }}
                        className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        {busyId === c.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <MoreVertical className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* An empty table must read as "nothing matched", never as a
                  broken page — and when the anonymity filter is what emptied
                  it, say so and offer the way out in the same breath. */}
              {!loading && visibleItems.length === 0 && hasLoadedFirst && !loadError && (
                <tr>
                  <td colSpan={8} className="px-3 py-12 text-center text-sm text-gray-500">
                    {anonFilterActive && hiddenAnonCount !== null && hiddenAnonCount > 0 ? (
                      <>
                        <div>
                          {t('contacts.empty.allAnon', {
                            n: hiddenAnonCount,
                            s: hiddenAnonCount === 1 ? '' : 's',
                          })}
                        </div>
                        <div className="mt-3 flex items-center justify-center gap-2">
                          <button
                            onClick={toggleShowAnonymous}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-primary-800 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100"
                          >
                            <Eye className="w-3.5 h-3.5" /> {t('contacts.anon.show')}
                          </button>
                          {(filters.verified || filters.blocked) && (
                            <button
                              onClick={clearAllFilters}
                              className="px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50"
                            >
                              {t('contacts.empty.clearFilters')}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div>{t('contacts.empty.noMatch')}</div>
                        {canClearFilters && (
                          <button
                            onClick={clearAllFilters}
                            className="mt-3 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50"
                          >
                            {t('contacts.empty.clearFilters')}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Footer / pagination */}
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-100 bg-white">
            <span className="text-xs text-gray-500">
              {loading ? 'Loading…' : hasLoadedFirst ? `Showing ${visibleCount}` : ''}
            </span>
            {cursor && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="text-xs px-3 py-1 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={buildRowMenu(menu.contact)}
          onClose={() => setMenu(null)}
        />
      )}

      {/* Subtle inline toast */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-md bg-gray-900 text-white text-xs shadow-lg max-w-md text-center">
          {toast}
        </div>
      )}

      {composeOpen && tenantCode && user?.uid && (
        <NewConversationModal
          tenantCode={tenantCode}
          agentUid={user.uid}
          prefill={
            composeFor
              ? { name: composeFor.name, email: composeFor.email, phone: composeFor.phone }
              : undefined
          }
          onClose={() => {
            setComposeOpen(false);
            setComposeFor(null);
          }}
          onCreated={(chatId) => {
            setComposeOpen(false);
            setComposeFor(null);
            navigate(`/inbox/${chatId}`);
          }}
        />
      )}
    </div>
  );
}

function Th({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <th
      onClick={onClick}
      className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide cursor-pointer hover:text-gray-900 select-none"
    >
      {label}
    </th>
  );
}

function FilterTri({
  label,
  value,
  onChange,
}: {
  label: string;
  value: FilterChoice;
  onChange: (v: FilterChoice) => void;
}) {
  return (
    <div className="flex items-center gap-2 px-1 py-1.5">
      <span className="flex-1 text-xs text-gray-700">{label}</span>
      <button
        onClick={() => onChange(value === '' ? 'yes' : value === 'yes' ? 'no' : '')}
        className={`text-[10px] font-medium px-2 py-0.5 rounded border ${
          value === 'yes'
            ? 'bg-green-100 text-green-800 border-green-200'
            : value === 'no'
            ? 'bg-red-100 text-red-800 border-red-200'
            : 'bg-gray-50 text-gray-500 border-gray-200'
        }`}
      >
        {value === '' ? 'any' : value}
      </button>
    </div>
  );
}
